/**
 * Free Delivery Notice
 * Dynamically calculates and displays free delivery eligibility on product pages
 * Updated: Shows message immediately on page load (no interaction required)
 */

class FreeDeliveryNotice {
  constructor(element) {
    this.element = element;
    this.threshold = parseFloat(element.dataset.threshold);
    this.productPrice = parseFloat(element.dataset.productPrice);
    const variantPricesRaw = this.element.dataset.variantPrices;
    this.variantPrices = variantPricesRaw ? JSON.parse(variantPricesRaw) : {};
    this.messageContainer = element.querySelector('.free-delivery-notice__message');
    this.textElement = element.querySelector('.free-delivery-notice__text');

    this.init();
  }

  init() {
    // Initial calculation - show immediately
    this.updateNotice();

    // Listen for quantity changes
    this.observeQuantityChanges();

    // Listen for variant changes
    this.observeVariantChanges();

    // Listen for add to cart button clicks
    this.observeAddToCart();

    // Listen for cart updates (when user adds to cart from any page)
    document.addEventListener('cart:updated', () => this.updateNotice());

    // Shopify theme events (if your theme uses them)
    if (window.Shopify && window.Shopify.theme) {
      document.addEventListener('shopify:section:load', () => this.updateNotice());
    }
  }

  observeAddToCart() {
    const addToCartButtons = document.querySelectorAll('[name="add"], button[type="submit"].product-form__submit, .product-form__submit');

    addToCartButtons.forEach(button => {
      button.addEventListener('click', () => {
        const pollCart = (attempts = 0) => {
          if (attempts >= 5) return;
          setTimeout(() => {
            this.updateNotice();
            pollCart(attempts + 1);
          }, 200 * (attempts + 1));
        };
        pollCart();
      });
    });

    document.addEventListener('cart:updated', () => {
      this.updateNotice();
    });
  }

  observeQuantityChanges() {
    const quantityInput = document.querySelector('input[name="quantity"]');
    const minusButtons = document.querySelectorAll('.quantity__button[name="minus"]');
    const plusButtons = document.querySelectorAll('.quantity__button[name="plus"]');

    if (quantityInput) {
      quantityInput.addEventListener('change', () => this.updateNotice());
      quantityInput.addEventListener('input', () => this.updateNotice());
    }

    minusButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => this.updateNotice(), 50);
      });
    });

    plusButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(() => this.updateNotice(), 50);
      });
    });

    if (quantityInput) {
      const observer = new MutationObserver(() => this.updateNotice());
      observer.observe(quantityInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  observeVariantChanges() {
    const variantInput = document.querySelector(
      'input[name="id"][type="hidden"], select[name="id"]'
    );
    if (!variantInput) return;

    let lastVariantId = variantInput.value;

    // Poll the input value - JS property changes don't trigger MutationObserver
    setInterval(() => {
      if (variantInput.value !== lastVariantId) {
        lastVariantId = variantInput.value;
        if (this.variantPrices[lastVariantId] !== undefined) {
          this.productPrice = this.variantPrices[lastVariantId];
          this.updateNotice();
        }
      }
    }, 100);

    // Belt and braces: change event for themes that fire it
    variantInput.addEventListener('change', () => {
      const id = variantInput.value;
      if (this.variantPrices[id] !== undefined) {
        this.productPrice = this.variantPrices[id];
        this.updateNotice();
      }
    });
  }

  async updateNotice() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      const quantityInput = document.querySelector('input[name="quantity"]');
      const currentQuantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

const currentCartTotal = cart.total_price / 100;
const remaining = this.threshold - currentCartTotal;

this.updateDisplay(remaining, currentCartTotal);

    } catch (error) {
      console.error('Error calculating free delivery:', error);
      this.element.style.display = 'none';
    }
  }

  updateDisplay(remaining, currentCartTotal) {
    const previousStatus = this.messageContainer.dataset.deliveryStatus;
    let newStatus;
    let message;

    if (currentCartTotal >= this.threshold) {
      newStatus = 'qualified';
      message = 'You have qualified for FREE delivery';
      this.element.style.display = 'block';
    } else if (remaining <= 0) {
      newStatus = 'qualified';
      message = 'Add this product for FREE delivery';
      this.element.style.display = 'block';
    } else {
      newStatus = 'under-threshold';
      const formattedRemaining = this.formatCurrency(remaining);
      message = `Add ${formattedRemaining} more for free delivery`;
      this.element.style.display = 'block';
    }

    this.messageContainer.dataset.deliveryStatus = newStatus;

    if (previousStatus !== newStatus && previousStatus !== 'calculating') {
      this.messageContainer.dataset.statusChanged = 'true';
      setTimeout(() => {
        this.messageContainer.dataset.statusChanged = 'false';
      }, 300);
    }

    this.textElement.style.opacity = '0';
    setTimeout(() => {
      this.textElement.textContent = message;
      this.textElement.style.opacity = '1';
    }, 150);
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const noticeElements = document.querySelectorAll('.free-delivery-notice');
  noticeElements.forEach(element => {
    new FreeDeliveryNotice(element);
  });
});

// Re-initialize if sections are reloaded (Shopify theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    const noticeElements = document.querySelectorAll('.free-delivery-notice');
    noticeElements.forEach(element => {
      if (!element.classList.contains('initialized')) {
        new FreeDeliveryNotice(element);
        element.classList.add('initialized');
      }
    });
  });
}

// -------------------------------------------------------
// Cart page notice updater
// -------------------------------------------------------
function updateCartNotice() {
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => {
      const notice = document.querySelector('.cart-free-delivery');
      if (!notice) return;

      const threshold = parseFloat(notice.dataset.threshold);
      const cartTotal = cart.total_price / 100;
      const remaining = threshold - cartTotal;
      const percent = Math.min((cartTotal / threshold) * 100, 100);

      const fill = notice.querySelector('.cart-free-delivery__bar-fill');
      const message = notice.querySelector('.cart-free-delivery__message');

    const bar = notice.querySelector('.cart-free-delivery__bar-wrapper');
if (bar) bar.style.setProperty('--fill-width', percent + '%');

      if (message) {
        if (cartTotal >= threshold) {
          message.textContent = '🎉 You have qualified for FREE delivery 🎉';
          message.classList.add('cart-free-delivery__message--qualified');
        } else {
          const formatted = new Intl.NumberFormat('en-GB', {
            style: 'currency', currency: 'GBP',
            minimumFractionDigits: 2, maximumFractionDigits: 2
          }).format(remaining);
          message.textContent = `🚚 Add ${formatted} more for FREE delivery 🚚`;
          message.classList.remove('cart-free-delivery__message--qualified');
        }
      }
    });
}

// Dawn fires this custom event when the cart is updated via Ajax
document.addEventListener('cart:updated', updateCartNotice);

// Fallback: watch for quantity button clicks on the cart page and poll
document.addEventListener('click', (e) => {
  const isCartQtyButton = e.target.closest('.quantity__button, [name="plus"], [name="minus"]');
  if (isCartQtyButton && document.querySelector('.cart-free-delivery')) {
    // Poll a few times to catch the Ajax response
    [300, 600, 1000, 1500].forEach(delay => {
      setTimeout(updateCartNotice, delay);
    });
  }
});